/* /heute-Kopf (v8 M19, 22.10.2026): Morgenbriefing, „Jetzt dran", „Deine 3 wichtigsten Dinge", Übung des Tages.
   Eine Abfrage (GET /api/heute-cockpit), lädt jede Minute neu, damit „Jetzt dran" nach dem Terminende verschwindet. */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Clock, CheckCircle2, Circle, GraduationCap, Sunrise } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/api';

const BADGE = {
  'Überfällig': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  'Follow-up': 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  Deal: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
};

export default function HeuteCockpit({ onGeaendert }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState('');
  const lade = useCallback(() => api.get('/heute-cockpit').then(r => setD(r.data)).catch(() => setD(null)), []);
  useEffect(() => { lade(); const t = setInterval(lade, 60000); return () => clearInterval(t); }, [lade]);
  if (!d) return null;

  const abhaken = async (i) => {
    setBusy(i.key);
    try {
      if (i.art === 'aufgabe') await api.post(`/aufgaben/${i.ref}/erledigt`);
      else await api.post(`/heute-cockpit/top3/${encodeURIComponent(i.key)}/abhaken`);
      await lade(); onGeaendert?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Nicht abgehakt'); }
    setBusy('');
  };
  const j = d.jetzt_dran, t = d.top3;

  return (
    <div className="space-y-3" data-testid="heute-cockpit">
      {d.briefing?.text && (
        <p className="flex items-start gap-2 text-sm" data-testid="heute-briefing"><Sunrise className="w-4 h-4 mt-0.5 flex-none text-marke" />{d.briefing.text}</p>
      )}
      <div className={`grid gap-3 ${j ? 'lg:grid-cols-2' : ''}`}>
        {j && (
          <Card className="border-marke/40" data-testid="jetzt-dran"><CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-marke"><Clock className="w-3.5 h-3.5" />Jetzt dran</div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-2xl font-extrabold tabular-nums">{j.laeuft ? 'läuft' : j.minuten_bis === 0 ? 'jetzt' : `in ${j.minuten_bis} Min.`}</span>
              <span className="text-sm text-muted-foreground">{j.uhrzeit} Uhr · {j.art_label}{j.kurs ? ` · ${j.kurs}` : ''}</span>
            </div>
            <p className="font-semibold">{j.name || 'ohne Namen'}{j.herkunft && <span className="font-normal text-muted-foreground"> · {j.herkunft}</span>}</p>
            {j.aktionen?.length > 0 && (
              <div className="flex flex-wrap gap-2">{j.aktionen.map(a => (
                <Link key={a.link + a.label} to={a.link} className="rounded-md border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted">{a.label}</Link>
              ))}</div>
            )}
            {j.danach?.length > 0 && <p className="text-xs text-muted-foreground">Danach: {j.danach.map(x => `${x.uhrzeit} ${x.art_label}${x.name ? ` (${x.name})` : ''}`).join(' · ')}</p>}
          </CardContent></Card>
        )}
        {t?.gesamt > 0 && (
          <Card data-testid="top3"><CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-bold">Deine 3 wichtigsten Dinge</h2>
              <span className="text-xs text-muted-foreground tabular-nums">{t.erledigt} von {t.gesamt} erledigt</span>
            </div>
            <ul className="space-y-1.5">{t.items.map(i => (
              <li key={i.key} className={`flex items-start gap-2 text-sm ${i.erledigt ? 'opacity-60' : ''}`}>
                <button disabled={i.erledigt || busy === i.key} onClick={() => abhaken(i)} title="Abhaken" className="mt-0.5 flex-none">
                  {i.erledigt ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-muted-foreground hover:text-foreground" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${BADGE[i.badge] || ''}`}>{i.badge}</span>
                    {i.link ? <Link to={i.link} className={`hover:underline ${i.erledigt ? 'line-through' : ''}`}>{i.titel}</Link> : <span className={i.erledigt ? 'line-through' : ''}>{i.titel}</span>}
                  </div>
                  {i.zusatz && <p className="text-xs text-muted-foreground">{i.zusatz}</p>}
                </div>
              </li>
            ))}</ul>
          </CardContent></Card>
        )}
      </div>
      {d.uebung && (
        <Link to={d.uebung.link} className="flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2 text-sm hover:bg-muted/50" data-testid="uebung-des-tages">
          <GraduationCap className="w-4 h-4 text-marke flex-none" />
          <span className="min-w-0"><span className="font-semibold">Übung des Tages:</span> {d.uebung.zielgruppe_label} · {d.uebung.einwand_label}</span>
        </Link>
      )}
    </div>
  );
}
